import React from 'react';

import { useJobProspectsState, useJobProspectsActions } from './JobProspectsContext';
import { subscribeToPremium } from './restApi';
import PageSectionTitle from '../../Shared/UI/PageSectionTitle/components/PageSectionTitle';
import Button from '../../Shared/UI/Button/components/Button';
import LocationFilterSettings from './LocationFilterSettings';
import JobTitlePreferenceSettings from './JobTitlePreferenceSettings';
import styles from './JobProspectsSettings.module.scss';

const JobProspectsSettings = () => {
  const state = useJobProspectsState();
  const {
    onResponse,
    onError,
    setEnrolled,
    setSubscribing,
    openPaymentMethodModal,
  } = useJobProspectsActions();

  const tier = state?.tier;
  const subscription = state?.subscription;
  const hasPaymentMethod = state?.hasPaymentMethod;
  const subscribing = state?.subscribing;

  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

  const handleUpgradeToPremium = () => {
    if (!hasPaymentMethod) {
      openPaymentMethodModal();
      return;
    }

    setSubscribing(true);
    subscribeToPremium({
      onResponse: (data) => {
        setSubscribing(false);
        setEnrolled(true, 'premium');
        onResponse({ ...data, tier: 'premium' });
      },
      onError: (data) => {
        setSubscribing(false);
        onError(data);
      },
      body: { authenticityToken },
    });
  };

  if (subscription && subscription.feeWaived) {
    return (
      <div className={styles.settings}>
        <PageSectionTitle text="Job Recommendations Settings" />
        <div className={styles.statusCard}>
          <p className={styles.tierLabel}>Premium Job Recommendations</p>
          <span className={styles.badge}>Fee Waived</span>
          <p className={styles.description}>
            Daily AI-powered job matching tailored to your skills and experience.
          </p>
        </div>
        <LocationFilterSettings />
        <JobTitlePreferenceSettings />
      </div>
    );
  }

  if (subscription && subscription.groupPaid) {
    return (
      <div className={styles.settings}>
        <PageSectionTitle text="Job Recommendations Settings" />
        <div className={styles.statusCard}>
          <p className={styles.tierLabel}>Premium Job Recommendations</p>
          <span className={styles.badge}>
            Paid by {subscription.groupName || 'your group'}
          </span>
          <p className={styles.description}>
            You have premium access at no cost through your membership in{' '}
            {subscription.groupName || 'your group'}. This includes daily
            AI-powered job matching tailored to your skills and experience.
          </p>
          <p className={styles.description}>
            This subscription is managed by your group. If you leave the group
            or the group discontinues this benefit, your account will switch to
            the free tier.
          </p>
        </div>
        <LocationFilterSettings />
        <JobTitlePreferenceSettings />
      </div>
    );
  }

  if (subscription && subscription.status === 'active' && subscription.canceledAt) {
    const endDate = new Date(subscription.currentPeriodEnd).toLocaleDateString();
    return (
      <div className={styles.settings}>
        <PageSectionTitle text="Job Recommendations Settings" />
        <div className={styles.statusCard}>
          <p className={styles.tierLabel}>Premium Job Recommendations</p>
          <p className={styles.description}>
            Your subscription has been canceled and will remain active until {endDate}.
          </p>
          <p className={styles.description}>
            After this date, your job recommendations will switch to the free tier (once per month).
          </p>
          <a href="/your_account" className={styles.manageLink}>
            Manage Subscription
          </a>
        </div>
        <LocationFilterSettings />
        <JobTitlePreferenceSettings />
      </div>
    );
  }

  if (subscription && (subscription.status === 'active' || subscription.status === 'past_due')) {
    const nextBilling = new Date(subscription.currentPeriodEnd).toLocaleDateString();
    return (
      <div className={styles.settings}>
        <PageSectionTitle text="Job Recommendations Settings" />
        <div className={styles.statusCard}>
          <p className={styles.tierLabel}>
            Premium Job Recommendations -- $19.99/month + applicable tax
          </p>
          <p className={styles.description}>
            Status: {subscription.status === 'active' ? 'Active' : 'Past due'}
          </p>
          <p className={styles.description}>Next billing date: {nextBilling}</p>
          <p className={styles.description}>
            Daily AI-powered job matching tailored to your skills and experience.
          </p>
          <a href="/your_account" className={styles.manageLink}>
            Manage Subscription
          </a>
        </div>
        <LocationFilterSettings />
        <JobTitlePreferenceSettings />
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <PageSectionTitle text="Job Recommendations Settings" />
      <div className={styles.statusCard}>
        <p className={styles.tierLabel}>Free Tier</p>
        <p className={styles.description}>
          You receive job recommendations once per month, matched to your profile.
        </p>
      </div>

      <div className={styles.upgradeCard}>
        <p className={styles.tierLabel}>
          Upgrade to Premium -- $19.99/month + applicable tax
        </p>
        <ul className={styles.featureList}>
          <li>Daily AI-powered job matching tailored to your skills and experience</li>
          <li>30 AI-generated resumes per month, customized to each job</li>
        </ul>
        <Button
          variant="primary"
          onClick={handleUpgradeToPremium}
          disabled={subscribing}
        >
          {subscribing ? 'Subscribing...' : 'Upgrade to Premium'}
        </Button>
      </div>
      <LocationFilterSettings />
      <JobTitlePreferenceSettings />
    </div>
  );
};

export default JobProspectsSettings;
